import React, { useState } from 'react';
import { Box, CardContent, Divider, Skeleton, Typography, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { ContentViewType } from 'global';
import { CreditApplication } from '@trnsact/trnsact-shared-types';
import { useScorecard } from '../hooks/useScorecard';
import { ScoreSummarySection } from '../components/ScoreSummarySection';
import { ApplicantDataForm } from '../components/ApplicantDataForm';
interface Props {
  vo: any;
  refetchVO: any;
  vp: any;
  account: any;
  equipmentData: any;
  refetchEquipment: any;
  equipmentLoading: any;
  userProfile: any;
  refetchCreditApp: any;
  creditApplication: CreditApplication;
  portalConfigurationDataLoading: any;
  creditAppOwners: any[];
  cs: any;
  refetchCreditSubmission?: any;
  contentViewType?: ContentViewType;
}

export const ScoreCardV2 = ({
  vo,
  refetchVO,
  refetchCreditSubmission,
  vp,
  account,
  equipmentData,
  refetchEquipment,
  equipmentLoading,
  refetchCreditApp,
  cs,
  creditApplication,
  portalConfigurationDataLoading,
  contentViewType = ContentViewType.Desktop,
  creditAppOwners,
}: Props) => {
  const {
    scorecardCriteria,
    isLoading,
    totalScore,
    band,
    bandColor,
    breakDown,
    creditLimit,
    applicantData,
    listOfFacts,
    stringFactOptions,
    manualFields,
    setManualFields,
    handleSaveScoreCard,
  } = useScorecard(creditApplication, equipmentData, vo, cs, account, portalConfigurationDataLoading, refetchVO);

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleFieldChange = (key: string, value: any) => {
    setManualFields(f => ({ ...f, [key]: value }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await handleSaveScoreCard(refetchCreditSubmission);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving scorecard:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CardContainer
      title="Scorecard"
      variant="elevation"
      contentViewType={contentViewType}
      sx={{ minHeight: 800 }}
      actionBtnProps={
        !isLoading && scorecardCriteria
          ? {
              label: saveSuccess ? 'Saved' : 'Save',
              onClick: handleSave,
              disabled: isSaving,
              startIcon: isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : saveSuccess ? (
                <CheckCircleIcon />
              ) : (
                <SaveIcon />
              ),
              variant: saveSuccess ? 'outlined' : 'contained',
              color: saveSuccess ? 'success' : 'primary',
              sx: {
                minWidth: 110,
                transition: 'all 0.3s ease-in-out',
                ...(saveSuccess && {
                  animation: 'pulse 0.5s ease-in-out',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                    },
                    '100%': {
                      transform: 'scale(1)',
                    },
                  },
                }),
              },
            }
          : undefined
      }
    >
      {!isLoading && scorecardCriteria && (
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Scorecard results: {new Date().toLocaleDateString()}
          </Typography>
        </Box>
      )}
      <CardContent style={{ padding: 0 }}>
        {isLoading || !scorecardCriteria ? (
          <Box sx={{ mx: 2, mt: 1, mb: 3, p: 4 }}>
            {/* Score Summary Skeleton - matches Grid layout */}
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {/* Total Score Card */}
              <Box sx={{ flex: '1 1 300px' }}>
                <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 3 }} />
              </Box>

              {/* Credit Band Card */}
              <Box sx={{ flex: '1 1 300px' }}>
                <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 2 }} />
              </Box>

              {/* Score Breakdown Card */}
              <Box sx={{ flex: '1 1 300px' }}>
                <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 2 }} />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Applicant Data Form Skeleton */}
            <Box sx={{ px: 2, pb: 2 }}>
              <Skeleton variant="text" width={120} height={24} sx={{ mb: 2 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                {[...Array(12)].map((_, i) => (
                  <Box key={i}>
                    <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <ScoreSummarySection
              totalScore={totalScore}
              band={band}
              bandColor={bandColor}
              breakDown={breakDown}
              creditLimit={creditLimit}
            />

            <Divider sx={{ mb: 3 }} />

            <ApplicantDataForm
              applicantData={applicantData}
              listOfFacts={listOfFacts}
              stringFactOptions={stringFactOptions}
              manualFields={manualFields}
              onFieldChange={handleFieldChange}
            />
          </>
        )}
      </CardContent>
    </CardContainer>
  );
};
